import React, { useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { PageHeader, Panel, Stat, Empty } from "@/components/Bits";

export default function Reports() {
  const { tenantId } = useTenant();
  const { data } = useQuery({
    queryKey: ["reports", tenantId],
    queryFn: () => api.get(`/reports/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const audit = useQuery({
    queryKey: ["audit"],
    queryFn: () => api.get(`/audit`).then((r) => r.data),
  });

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;
  if (!data) return <Empty msg="Loading…" />;

  return (
    <>
      <PageHeader title="Reports" kicker="Executive summary · Audit trail"><WeeklyPdfButton tenantId={tenantId} /></PageHeader>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Endpoints" value={data.endpoints} />
          <Stat label="Incidents" value={data.incidents} sub={`${data.critical_incidents} critical`} tone="critical" />
          <Stat label="Emails Scanned" value={data.emails_scanned} sub={`${data.phishing_blocked} quarantined`} tone="high" />
          <Stat label="Vulnerabilities" value={data.vulnerabilities} tone="ai" />
        </div>
        <Panel kicker="Audit Trail" title="Response actions">
          <div className="max-h-[500px] overflow-y-auto divide-y divide-white/5">
            {(audit.data || []).map((a) => (
              <div key={a.id} className="px-4 py-2 text-xs flex items-center gap-3">
                <span className="font-mono text-[10px] text-zinc-500 w-40">{new Date(a.ts).toLocaleString()}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 w-24">{a.action}</span>
                <span className="text-zinc-300 flex-1">{a.result}</span>
                <span className="font-mono text-[10px] text-zinc-500">{a.actor}</span>
              </div>
            ))}
            {(audit.data || []).length === 0 && <Empty msg="No audited actions yet" />}
          </div>
        </Panel>
      </div>
    </>
  );
}

function WeeklyPdfButton({ tenantId }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/weekly/${tenantId}/pdf`, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "cyboscan-weekly-report.pdf";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click(); URL.revokeObjectURL(url);
      toast.success("Weekly PDF report downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the report"); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="weekly-pdf-download" onClick={download} disabled={busy} className="border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
      <FileDown size={13} /> {busy ? "Building PDF…" : "Weekly PDF report"}
    </button>
  );
}
